import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2, AlertTriangle } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { consumePostLoginRedirect } from "../lib/postLoginRedirect";

/**
 * AuthCallback — riceve session_id nel fragment dopo redirect Google.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [error, setError] = useState("");
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = location.hash || window.location.hash || "";
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const sessionId = params.get("session_id");
    if (!sessionId) {
      navigate("/login", { replace: true });
      return;
    }
    // Clean the URL fragment
    try { window.history.replaceState(null, "", window.location.pathname); } catch (err) { console.debug("[auth-callback] history replace failed", err); }

    (async () => {
      try {
        const { data } = await api.post("/auth/google/exchange", { session_id: sessionId });
        if (data?.needs_profile && data?.pending_token) {
          // Nuovo utente Google — NON è ancora in DB. Passa il token firmato al
          // form di completamento profilo che effettuerà la creazione atomica.
          try {
            sessionStorage.setItem("google_pending_token", data.pending_token);
            sessionStorage.setItem("google_pending_email", data.google_email || "");
            sessionStorage.setItem("google_pending_name", data.google_name || "");
          } catch (err) { console.debug("[auth-callback] sessionStorage failed", err); }
          navigate("/completa-profilo", { replace: true });
          return;
        }
        try { await refresh?.(); } catch (err) { console.debug("[auth-callback] refresh failed", err); }
        if (data?.needs_profile) {
          // Fallback per account Google legacy già presenti in DB (rp_name/discord_id vuoti)
          navigate("/completa-profilo", { replace: true });
        } else if (data?.pending_approval) {
          navigate("/registrazione-in-attesa", { replace: true });
        } else {
          navigate(consumePostLoginRedirect(), { replace: true });
        }
      } catch (e) {
        const detail = e?.response?.data?.detail;
        setError(typeof detail === "string" ? detail : "Autenticazione Google fallita");
      }
    })();
  }, [location.hash, navigate, refresh]);

  return (
    <div className="min-h-screen bg-[#0A1F18] flex items-center justify-center px-4" data-testid="auth-callback">
      <div className="text-center max-w-md">
        {error ? (
          <>
            <AlertTriangle className="h-14 w-14 text-rose-400 mx-auto mb-4" />
            <div className="uppercase tracking-widest text-xs text-rose-300 mb-2">Errore autenticazione</div>
            <p className="text-[#F5E6B8] text-sm mb-6">{error}</p>
            <button
              onClick={() => navigate("/login", { replace: true })}
              className="rounded-full bg-[var(--lssd-khaki,#A78E44)] text-[#0A1F18] font-bold uppercase tracking-widest text-xs px-5 py-2.5"
              data-testid="auth-callback-back"
            >
              Torna al Login
            </button>
          </>
        ) : (
          <>
            <Loader2 className="h-10 w-10 text-[#D4AF37] animate-spin mx-auto mb-4" />
            <div className="uppercase tracking-widest text-xs text-[#D4AF37] mb-2">Autenticazione in corso</div>
            <p className="text-[#F5E6B8]">Verifica della sessione Google…</p>
          </>
        )}
      </div>
    </div>
  );
}
